import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { NomineeEntity, NomineeType, Player, Team, Translated } from '../types';
import { NomineeEntityType } from '../types';
import { getEntityLink } from '../utils/apis';
import { getNomineePlayer, getNomineeTeam, getPlayerPhoto } from '../utils/nomineeHelper';
import { isTeamApp as checkIfTeamApp } from '../utils/utils';

import { useOptions } from './useOptions';
import { useTranslatedEntity } from './useTranslatedEntity';

export const getBasicEntityFromNominee = (nominee: NomineeType, appType: string): Partial<NomineeEntity> => {
  const isTeamApp = checkIfTeamApp(appType);

  const { id, internationalName } = isTeamApp ? getNomineeTeam(nominee) : getNomineePlayer(nominee);

  return { id, internationalName, type: isTeamApp ? NomineeEntityType.Team : NomineeEntityType.Player };
};

const createPlayerEntity = (
  player: Translated<Player>,
  team: Translated<Team>,
  photo: string,
): Partial<NomineeEntity> => {
  if (!player) return;

  const { logoUrl: badge, typeIsNational: isNationalTeam } = team;
  const badgeFallback = isNationalTeam ? 'ntc-generic-badge-01' : 'club-generic-badge';

  return {
    id: player.id,
    internationalName: player.internationalName,
    firstName: player?.firstName,
    lastName: player?.lastName,
    shortName: player?.shortName,
    name: player?.name,
    photo,
    fallback: 'generic-head',
    badge,
    badgeFallback,
    teamName: team.displayName,
    type: NomineeEntityType.Player,
  };
};

const createTeamEntity = (team: Translated<Team>): Partial<NomineeEntity> => {
  if (!team) return;

  const fallback = team.typeIsNational ? 'ntc-generic-badge-01' : 'club-generic-badge';

  return {
    id: team.id,
    internationalName: team.internationalName,
    name: team?.displayName,
    photo: team?.logoUrl,
    badgeFallback: fallback,
    type: NomineeEntityType.Team,
  };
};

export const useNomineeEntity = (nominee: NomineeType): NomineeEntity => {
  const {
    i18n: { language },
  } = useTranslation();
  const options = useOptions();
  const isTeamApp = checkIfTeamApp(options);

  const player = getNomineePlayer(nominee);
  const translatedPlayer = useTranslatedEntity(player);

  const team = getNomineeTeam(nominee);
  const translatedTeam = useTranslatedEntity(team);

  const competitionId = useMemo(() => nominee?.matchDetails?.competition?.id, [nominee]);

  const partialEntity = useMemo(() => {
    if (isTeamApp) {
      return createTeamEntity(translatedTeam);
    } else {
      const photo = getPlayerPhoto(nominee);
      return createPlayerEntity(translatedPlayer, translatedTeam, photo);
    }
  }, [isTeamApp, nominee, translatedPlayer, translatedTeam]);

  if (!partialEntity) return;

  const link = getEntityLink(partialEntity.id, competitionId, language, isTeamApp ? 'team' : 'player');

  return {
    ...partialEntity,
    link,
  } as NomineeEntity;
};
